// apps/web/src/pages/settings/LeaveTypesPage.tsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Alert } from '../../components/ui/Alert'
import api from '../../lib/api'

interface LeaveType {
  id: string
  name: string
  color: string
  isPaid: boolean
  requiresApproval: boolean
  isActive: boolean
}

export function LeaveTypesPage() {
  const qc = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<LeaveType | null>(null)
  const [form, setForm] = useState({ name: '', color: '#6B7280', isPaid: true, requiresApproval: true })

  const { data: types = [], isLoading } = useQuery<LeaveType[]>({
    queryKey: ['leave-types-all'],
    queryFn: async () => (await api.get<LeaveType[]>('/leave-types/all')).data,
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing) await api.patch(`/leave-types/${editing.id}`, form)
      else await api.post('/leave-types', form)
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['leave-types-all'] }); setFormOpen(false) },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/leave-types/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['leave-types-all'] }),
  })

  const openCreate = () => {
    setEditing(null)
    setForm({ name: '', color: '#6B7280', isPaid: true, requiresApproval: true })
    setFormOpen(true)
  }

  const openEdit = (lt: LeaveType) => {
    setEditing(lt)
    setForm({ name: lt.name, color: lt.color, isPaid: lt.isPaid, requiresApproval: lt.requiresApproval })
    setFormOpen(true)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Tipi Permesso"
        breadcrumb={[{ label: 'Impostazioni', href: '/settings' }, { label: 'Tipi Permesso' }]}
        actions={<Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>Nuovo tipo</Button>}
      />

      <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden">
        {isLoading ? (
          <div className="py-10 text-center text-sm text-[var(--fc-foreground-muted)]">Caricamento...</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--fc-border)] bg-[var(--fc-surface-overlay)]">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Nome</th>
                <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Colore</th>
                <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Pagato</th>
                <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Richiede approvazione</th>
                <th className="px-4 py-3 text-left font-medium text-[var(--fc-foreground-muted)]">Stato</th>
                <th className="px-4 py-3 text-right font-medium text-[var(--fc-foreground-muted)]">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--fc-border)]">
              {types.map((lt) => (
                <tr key={lt.id} className="hover:bg-[var(--fc-surface-overlay)]">
                  <td className="px-4 py-3 font-medium text-[var(--fc-foreground)]">
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: lt.color }} />
                      {lt.name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[var(--fc-foreground-muted)] font-mono text-xs">{lt.color}</td>
                  <td className="px-4 py-3">{lt.isPaid ? 'Si' : 'No'}</td>
                  <td className="px-4 py-3">{lt.requiresApproval ? 'Si' : 'No'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${lt.isActive ? 'text-[var(--fc-success)]' : 'text-[var(--fc-foreground-muted)]'}`}>
                      {lt.isActive ? 'Attivo' : 'Disattivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon-sm" variant="ghost" aria-label="Modifica" onClick={() => openEdit(lt)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon-sm" variant="ghost" aria-label="Elimina" onClick={() => { if (window.confirm(`Disattivare il tipo permesso "${lt.name}"?`)) deleteMutation.mutate(lt.id) }}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={formOpen} onClose={() => setFormOpen(false)} title={editing ? `Modifica: ${editing.name}` : 'Nuovo tipo permesso'} size="sm">
        <div className="space-y-4">
          <Input label="Nome *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="es. Ferie" />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-[var(--fc-foreground)]">Colore</label>
            <input type="color" value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
              className="h-10 w-20 rounded border border-[var(--fc-border)] cursor-pointer" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isPaid} onChange={(e) => setForm((f) => ({ ...f, isPaid: e.target.checked }))} />
            Permesso pagato
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.requiresApproval} onChange={(e) => setForm((f) => ({ ...f, requiresApproval: e.target.checked }))} />
            Richiede approvazione team leader
          </label>
          {saveMutation.isError && <Alert variant="error">Errore durante il salvataggio.</Alert>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => setFormOpen(false)}>Annulla</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !form.name}>
              {saveMutation.isPending ? 'Salvataggio...' : editing ? 'Salva' : 'Crea'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
